import { Box, Card, Modal, Typography } from '@mui/material';
import React, { ReactNode, useId } from 'react';
import ErrorBanner from './errorhandling/ErrorBanner';
import { ErrorObject, errorSeverity } from '../types/errorhandling.types';

interface ModalDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  actions?: ReactNode;
  error?: string;
  errors?: ErrorObject;
  headerAction?: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg';
}

export const ModalDialog = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  actions,
  error,
  errors,
  headerAction,
  maxWidth = 'md',
}: ModalDialogProps) => {
  const titleId = useId();
  const subtitleId = useId();
  const maxWidthMap = {
    sm: { xs: '100%', sm: '500px' },
    md: { xs: '100%', sm: '700px' },
    lg: { xs: '100%', sm: '900px' },
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'auto',
      }}
    >
      <Card
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={subtitle ? subtitleId : undefined}
        sx={{
          width: maxWidthMap[maxWidth],
          margin: { xs: 0, sm: 'auto' },
          padding: 24,
          maxHeight: { xs: '100vh', md: '95vh' },
          overflow: 'auto',
          borderRadius: { xs: 0, sm: 1 },
          position: 'relative',
          '&:focus-visible': {
            outline: '2px solid var(--color-scifree-teal)',
            outlineOffset: '-2px',
          },
        }}
      >
        {error && (
          <ErrorBanner
            errorMessage={error}
            severity={errorSeverity.Error}
            layer={0}
          />
        )}
        {errors && (
          <ErrorBanner
            errors={errors}
            severity={errorSeverity.Error}
            layer={error ? 1 : 0}
          />
        )}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', md: 'center' },
            width: '100%',
            gap: { xs: 12, md: 0 },
            mb: 24,
          }}
        >
          <Box>
            <Typography id={titleId} variant="h4" gutterBottom>
              {title}
            </Typography>
            {subtitle && (
              <Typography id={subtitleId} variant="body2">
                {subtitle}
              </Typography>
            )}
          </Box>
          {headerAction && (
            <Box sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}>
              {headerAction}
            </Box>
          )}
        </Box>
        <Box>{children}</Box>
        {actions && (
          <Box
            display="flex"
            justifyContent="flex-end"
            gap={12}
            mt={24}
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              '& > button': {
                width: { xs: '100%', sm: 'auto' },
              },
            }}
          >
            {actions}
          </Box>
        )}
      </Card>
    </Modal>
  );
};
